import { useQuery } from 'react-query';
import {
    deviceStateQueryOptions,
    useDevice,
} from 'components/DeviceDetails/service';
import {
    checkFaultsAndAlarms,
    verifySingleStack,
} from 'components/DeviceDetails/helper';
import { useJwtToken } from 'helper';
import { getMasterConfig } from '../EditDeviceProfile/helper';

// Returns an array aligned with `packs`, each entry shaped like a react-query result
// ({ data, isLoading }) so existing call sites keep working.
export const usePackStateQueries = (packs = [], Thing_ID) => {
    const accessToken = useJwtToken();
    const thingState = useQuery(deviceStateQueryOptions(accessToken, Thing_ID));
    const packStateBySn = new Map();
    for (const master of thingState.data?.masters ?? []) {
        for (const pack of master.packs ?? []) {
            packStateBySn.set(pack.device_sn, pack.state ?? null);
        }
    }
    return packs.map((pack) => ({
        data: packStateBySn.get(getPackSerialNumber(pack)) ?? null,
        isLoading: thingState.isLoading,
        isError: thingState.isError,
    }));
};

export function buildTableData(packStateQueries, packs, device) {
    if (!packs) return [];
    return packs.map((pack, idx) => {
        const packState = packStateQueries[idx]?.data;
        const packSerialNumber = pack?.PACK_SN;
        const { hasFault, hasAlarm, faultCount, alarmCount } = checkFaultsAndAlarms(
            packSerialNumber,
            device?.active_alerts
        );
        return {
            ...packState,
            SoC: packState?.SoC ?? '--',
            SoH: packState?.SoH ?? '--',
            current: packState?.current ?? '--',
            charging: packState?.charging ?? '--',
            temp_c: packState?.temp_c ?? '--',
            PackName: pack.PackName,
            P_serialNumber: packSerialNumber,
            hasFault,
            hasAlarm,
            faultCount,
            alarmCount,
        };
    });
}

export const useBatteryModuleStates = (Thing_ID, BMS_ID) => {
    const { data: device, isLoading: isDeviceLoading } = useDevice(Thing_ID);
    const { SINGLE_BMS_ID } = verifySingleStack(device);
    const packs = getMasterConfig(
        device?.Config?.data?.MASTER_SNS,
        SINGLE_BMS_ID || BMS_ID
    )?.PACKS;

    const packStateQueries = usePackStateQueries(packs, Thing_ID);
    const tableData = buildTableData(packStateQueries, packs, device);

    const isLoading =
        isDeviceLoading || packStateQueries.some((query) => query.isLoading);

    return { tableData, isLoading };
};

const getPackSerialNumber = (pack) =>
    typeof pack === 'string' ? pack.trim() : pack?.PACK_SN;
